import Database from "@tauri-apps/plugin-sql";
import { Map } from "immutable";
import { assign, fromPromise, setup } from "xstate";

import { type BaseSpec } from "@moment/api-collab/api-types";
import { Store } from "@moment/api-collab/differential";
import { type SqliteTableDef } from "@moment/api-collab/differential/schema";
import {
	SqlitePersistence,
	type TableDiff,
	computeSchemaDiff,
	queryEvaluatorStateFromSqlite,
	runCreateTableIfNotExists,
	runEnsureBaseMetadataTable,
	runGetTableXInfo,
} from "@moment/api-collab/differential/sqlite";
import { type DatabaseTables } from "@moment/api-collab/differential/types";
import { Logger } from "@moment/logging";

import { getActiveDocumentBaseFilePath, getBasesDirPath, mkdir } from "~/data/filesystem";

const logger = new Logger("base.machine");

const loadActiveBaseFromDisk = fromPromise<Database, { documentId: string }>(async ({ input }) => {
	logger.info("Loading base spec from disk", { documentId: input.documentId });
	const basesDir = await getBasesDirPath(input.documentId);
	const activeBasePath = await getActiveDocumentBaseFilePath(input.documentId);
	await mkdir(basesDir, { recursive: true });

	return await Database.load(`sqlite:${activeBasePath}`);
});

const loadBaseSpecFromRemote = fromPromise<BaseSpec, { workspaceName: string; documentId: string }>(
	async ({ input }) => {
		logger.info("Loading base spec from remote", {
			workspaceName: input.workspaceName,
			documentId: input.documentId,
		});
		// TODO: Implement loading base spec from remote
		return {} as BaseSpec;
	}
);

const initializeStore = fromPromise<
	{ store: Store<DatabaseTables>; schemaDiff: Map<string, TableDiff> },
	{
		sqliteDatabase: Database;
		localBaseSpecReplica: () => BaseSpec;
		saveBaseSpec: (baseSpec: BaseSpec) => Promise<void>;
	}
>(async ({ input }) => {
	const { sqliteDatabase, localBaseSpecReplica, saveBaseSpec } = input;

	const baseSpec = localBaseSpecReplica();
	await runEnsureBaseMetadataTable(sqliteDatabase, baseSpec);

	// Create initial tables from base spec
	let schemaDiff = Map<string, TableDiff>();
	for (const [tableName, tableSpec] of Object.entries(baseSpec.tables)) {
		const desiredTable: SqliteTableDef = { tableName, columns: tableSpec.columns };
		await runCreateTableIfNotExists(sqliteDatabase, desiredTable);

		const currentTableInfo = await runGetTableXInfo(sqliteDatabase, tableName);
		const diff = computeSchemaDiff(currentTableInfo, desiredTable);
		schemaDiff = schemaDiff.set(tableName, diff);
	}

	const initialState = await queryEvaluatorStateFromSqlite(sqliteDatabase, baseSpec);
	const queryPersistence = new SqlitePersistence(sqliteDatabase, initialState);
	const baseSpecPersistence = {
		localBaseSpecReplica,
		saveBaseSpec,
	};

	// Create store with initial tables
	const store = new Store<DatabaseTables>(queryPersistence, baseSpecPersistence);

	return { store, schemaDiff };
});

export const baseMachine = setup({
	types: {
		context: {} as {
			documentPath?: string;
			workspaceName?: string;
			documentId: string;
			baseSpec?: BaseSpec;
			schemaDiff: Map<string, TableDiff>;
			sqliteDatabase?: Database;
			store?: Store<DatabaseTables>;
			error: Error | null;
		},
		input: {} as {
			documentPath?: string;
			workspaceName?: string;
			documentId: string;
			baseSpec?: BaseSpec;
		},
		events: {} as
			| { type: "base.opened" }
			| { type: "base.closed" }
			| { type: "base.specChanged"; payload: { baseSpec: BaseSpec } }
			| { type: "base.error"; payload: { error: Error } },
	},
	actors: {
		loadActiveBaseFromDisk,
		loadBaseSpecFromRemote,
		initializeStore,
	},
	guards: {
		hasDocumentPath: ({ context }) => Boolean(context.documentPath),
	},
	actions: {
		clearStore: assign(() => ({
			store: undefined,
			error: null,
		})),
	},
}).createMachine({
	id: "base",
	context: ({ input }) => ({
		documentPath: input.documentPath,
		workspaceName: input.workspaceName,
		documentId: input.documentId,
		baseSpec: input.baseSpec,
		schemaDiff: Map(),
		sqliteDatabase: undefined,
		error: null,
	}),
	initial: "idle",
	states: {
		idle: { on: { "base.opened": { target: "loading" } } },
		loading: {
			initial: "loadingSpec",
			states: {
				loadingSpec: {
					always: [
						{ guard: "hasDocumentPath", target: "loadingFromDisk" },
						{ target: "loadingFromRemote" },
					],
				},
				loadingFromDisk: {
					invoke: {
						src: "loadActiveBaseFromDisk",
						input: ({ context }) => ({ documentId: context.documentId! }),
						onDone: {
							actions: assign({ sqliteDatabase: ({ event }) => event.output }),
							target: "initializingStore",
						},
						onError: {
							actions: assign({ error: ({ event }) => event.error as Error }),
							target: "#base.error",
						},
					},
				},
				loadingFromRemote: {
					invoke: {
						src: "loadBaseSpecFromRemote",
						input: ({ context }) => ({
							workspaceName: context.workspaceName!,
							documentId: context.documentId,
						}),
						onDone: {
							actions: assign({ baseSpec: ({ event }) => event.output }),
							target: "initializingStore",
						},
						onError: {
							actions: assign({ error: ({ event }) => event.error as Error }),
							target: "#base.error",
						},
					},
				},
				initializingStore: {
					invoke: {
						src: "initializeStore",
						input: ({ context, self }) => ({
							localBaseSpecReplica: () => self.getSnapshot().context.baseSpec!,
							sqliteDatabase: context.sqliteDatabase!,
							saveBaseSpec: async (baseSpec: BaseSpec) => {
								self._parent?.send({
									type: "document.baseSpecChanged",
									payload: { base: baseSpec },
								});
								self.send({ type: "base.specChanged", payload: { baseSpec } });
							},
						}),
						onDone: {
							actions: assign({
								store: ({ event }) => event.output.store,
								schemaDiff: ({ event }) => event.output.schemaDiff,
							}),
							target: "ready",
						},
						onError: {
							actions: assign({ error: ({ event }) => event.error as Error }),
							target: "#base.error",
						},
					},
				},
				ready: { type: "final" },
			},
			onDone: { target: "running" },
		},
		running: {
			on: {
				"base.specChanged": {
					actions: assign({ baseSpec: ({ event }) => event.payload.baseSpec }),
					// target: "loading",
				},
				"base.closed": { target: "idle", actions: "clearStore" },
			},
		},
		error: { on: { "base.opened": { target: "loading" } } },
	},
});
